// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Games this browser has finished and not yet posted (M7-20).
//
// **Finishing a game wrote it down locally and posted nothing.** `submitReplay` has existed in
// `net/client.ts` since the account work and the only thing that ever called it was the manual
// migration panel — so a player who finished a game and closed the tab had a score no leaderboard
// would ever hear about. The native client grew a queue for the offline case; this is the same
// queue on this side, because the failure it prevents is not "offline", it is "the submission did
// not happen and nobody noticed".
//
// **This file is storage and nothing else, and that boundary is enforced.** Draining the queue
// lives in `net/replaySync.ts`, because the moment anything on the play path can reach the API
// client it can reach the Supabase SDK, and `scripts/check-anonymous.mjs` fails the build for
// exactly that: constructing a Supabase client reads storage, refreshes a session and can mint an
// identity, and creating an auth user in this project fires three other applications' triggers.
// FR-030 wants an anonymous game to appear in no log at all. So the machine writes to the queue,
// which is IndexedDB and the core, and the account chunk — which a visitor never downloads —
// empties it.
//
// **The two clients share the key and nothing else.** `submissionKey` lives in the core and is held
// identical by fixture, because the server's uniqueness constraint is `(profile_id,
// idempotency_key)` — a browser and a headset that disagreed about one game's key would post it
// twice. Everything below it is different by necessity: this side has IndexedDB and no
// transactions, that side has a journalled store and does the whole thing in one commit.
//
// **Which is why the queue is written first.** Without a transaction the writes that finish a game
// can be interrupted between any two of them, so the order is chosen for what a crash leaves
// behind: queue, then clear the interrupted game, then place the score. Crash after the queue write
// and the player resumes the game they were already offered, finishes it again, and produces the
// *same trace and therefore the same key* — one submission. Crash the other way round and the score
// is on the local table with nothing queued to post it, which nothing can repair.

import { submissionKey, type ReplayTrace } from '@core/replay/trace'
import { openDocuments, type DocumentStore } from './db'

/** Where entries live in the document store. */
export const OUTBOX_PREFIX = 'outbox/'

/**
 * How many unsent games are kept.
 *
 * **When it is full the oldest goes.** A player is far better served by losing the score they
 * earned a fortnight ago than the one they earned thirty seconds ago.
 */
export const MAX_QUEUED = 20

/** The first wait after a failure, milliseconds. */
export const FIRST_BACKOFF_MS = 30_000

/** The longest wait between attempts. Fifteen minutes. */
export const MAX_BACKOFF_MS = 900_000

/**
 * How many attempts before a game is given up on.
 *
 * **Given up on is not deleted.** The entry stays with the reason on it, so a player can be told
 * rather than left to notice a score that never appeared.
 */
export const MAX_ATTEMPTS = 12

/** Where one queued game has got to. */
export type OutboxState = 'queued' | 'accepted' | 'refused'

/** One game waiting to be posted. */
export interface OutboxEntry {
  /** The document key, which is the prefix and the idempotency key. */
  readonly id: string
  readonly idempotencyKey: string
  readonly queuedAt: number
  readonly attempts: number
  /** The wall-clock time before which there is no point trying again. */
  readonly notBefore: number
  readonly state: OutboxState
  readonly inputMethod: string
  readonly trace: ReplayTrace
  readonly replayId?: string
  readonly status?: string
  readonly trouble?: string
}

/** The wait before the next attempt, milliseconds. A server that names a time is believed. */
export function backoffMs(attempts: number, retryAfterSeconds?: number): number {
  if (retryAfterSeconds !== undefined && retryAfterSeconds > 0) {
    return Math.min(retryAfterSeconds * 1000, MAX_BACKOFF_MS)
  }
  return Math.min(FIRST_BACKOFF_MS * 2 ** Math.max(0, attempts - 1), MAX_BACKOFF_MS)
}

/**
 * Games this browser has finished and not yet posted.
 *
 * Opened like `LocalHistory`, and like it a store that would not open produces a queue that quietly
 * keeps nothing rather than a caller that has to handle the failure.
 */
export class ReplayOutbox {
  private readonly store: DocumentStore | undefined

  private constructor(store: DocumentStore | undefined) {
    this.store = store
  }

  static async open(store?: DocumentStore): Promise<ReplayOutbox> {
    return new ReplayOutbox(store ?? (await openDocuments()))
  }

  get available(): boolean {
    return this.store !== undefined
  }

  /** Everything queued, oldest first, whatever state it is in. */
  async entries(): Promise<readonly OutboxEntry[]> {
    if (this.store === undefined) return []
    const keys = (await this.store.keys()).filter((k) => k.startsWith(OUTBOX_PREFIX))
    const found: OutboxEntry[] = []
    for (const key of keys) {
      const one = await this.store.get<OutboxEntry>(key)
      // Skipped rather than deleted if it is not one: a score a player earned is server data that
      // has not reached the server yet, and a build that cannot read it may be replaced by one
      // that can.
      if (one !== undefined && typeof one.idempotencyKey === 'string' && one.trace !== undefined) {
        found.push(one)
      }
    }
    return found.sort((a, b) => a.queuedAt - b.queuedAt || (a.id < b.id ? -1 : 1))
  }

  /** The entries worth offering right now, oldest first. */
  async due(now: number): Promise<readonly OutboxEntry[]> {
    return (await this.entries()).filter((e) => e.state === 'queued' && e.notBefore <= now)
  }

  /** How many games are still waiting. The number a player is shown. */
  async waiting(): Promise<number> {
    return (await this.entries()).filter((e) => e.state === 'queued').length
  }

  /**
   * Queue one finished game.
   *
   * Keyed by the idempotency key, so the same game queued twice is one entry here as well as one
   * submission at the server — which is what a resumed game finished a second time produces.
   */
  async enqueue(trace: ReplayTrace, inputMethod: string, at: number): Promise<boolean> {
    if (this.store === undefined) return false
    const key = submissionKey(trace)
    const entry: OutboxEntry = {
      id: `${OUTBOX_PREFIX}${key}`,
      idempotencyKey: key,
      queuedAt: at,
      attempts: 0,
      notBefore: 0,
      state: 'queued',
      inputMethod,
      trace,
    }
    const written = await this.store.put(entry.id, entry)

    // `Math.max(0, …)` and not just the subtraction: a negative end index counts from the *end* of
    // the array, so `slice(0, -9)` on ten entries drops the oldest one instead of none of them.
    // Without it the queue started trimming at ten games and settled there — a cap of twenty that
    // silently held half that, which is a lost score every time.
    const all = await this.entries()
    const over = Math.max(0, all.length - MAX_QUEUED)
    for (const old of all.filter((e) => e.id !== entry.id).slice(0, over)) {
      await this.store.remove(old.id)
    }
    return written
  }

  /** The server took it. Recorded, and kept until the player has been shown. */
  async accepted(entry: OutboxEntry, replayId: string, status: string): Promise<void> {
    await this.store?.put(entry.id, {
      ...entry,
      state: 'accepted',
      replayId,
      status,
      // Cleared rather than left: a game that has been accepted has no trouble, and leaving the
      // last transient failure on it would show a player an error beside a score that got through.
      trouble: '',
    } satisfies OutboxEntry)
  }

  /** Not now. Count the attempt and work out when to try again. */
  async deferred(
    entry: OutboxEntry,
    now: number,
    trouble: string,
    retryAfterSeconds?: number,
  ): Promise<void> {
    const attempts = entry.attempts + 1
    const spent = attempts >= MAX_ATTEMPTS
    await this.store?.put(entry.id, {
      ...entry,
      attempts,
      // Out of attempts is not out of the store. See MAX_ATTEMPTS.
      state: spent ? 'refused' : 'queued',
      notBefore: spent ? 0 : now + backoffMs(attempts, retryAfterSeconds),
      trouble,
    } satisfies OutboxEntry)
  }

  /** The server will never take it. Kept, with the reason. */
  async refused(entry: OutboxEntry, trouble: string): Promise<void> {
    await this.store?.put(entry.id, {
      ...entry,
      attempts: entry.attempts + 1,
      state: 'refused',
      notBefore: 0,
      trouble,
    } satisfies OutboxEntry)
  }

  /** Take an entry out. Only for a player who asked, or a result already shown. */
  async forget(entry: OutboxEntry): Promise<void> {
    await this.store?.remove(entry.id)
  }
}
